<script setup lang="ts">
import { ref } from 'vue';
import { useRoute } from 'vue-router';
import router from '../../router';
import { useAuthStore } from '../../store/authStore';
import { useConfigStore } from '../../store/configStore';
import api from '../../api';

const route    = useRoute();
const token    = (route.params.id as string) || (route.query.token as string) || '';
const password = ref('');
const confirm  = ref('');
const phone    = ref('');
const loading  = ref(false);
const error    = ref('');
const store    = useAuthStore();
const config   = useConfigStore();

// If already authenticated, don't overwrite the session
if (store.isAuthenticated) {
  router.replace('/request');
}

const submit = async () => {
  error.value = '';
  if (!phone.value.trim()) {
    error.value = 'Phone number is required.'; return;
  }
  if (!password.value || password.value.length < 8) {
    error.value = 'Password must be at least 8 characters.'; return;
  }
  if (password.value !== confirm.value) {
    error.value = 'Passwords do not match.'; return;
  }
  loading.value = true;
  try {
    const res  = await api.post('customer/auth/onboard', { token, password: password.value, phone: phone.value.trim() });
    const data = res.data.data;
    store.setSession(data.token, data.user);
    router.push('/request');
  } catch (err: any) {
    error.value = err.response?.data?.message || 'This invite link is invalid or has expired.';
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <div class="onboard-page">
    <div class="onboard-card">
      <div class="onboard-logo">
        <span class="logo-text">{{ config.getCompanyName }}<span class="dot">.</span></span>
      </div>

      <h1 class="onboard-title">Set your password</h1>
      <p class="onboard-sub">Create a password to activate your account and get started.</p>

      <div v-if="error" class="onboard-error">{{ error }}</div>

      <form @submit.prevent="submit" class="onboard-form">
        <div class="form-group">
          <label>Phone number <span class="req">*</span></label>
          <input
            v-model="phone"
            type="tel"
            placeholder="+254 7XX XXX XXX"
            autocomplete="tel"
            required
          />
        </div>
        <div class="form-group">
          <label>Password <span class="req">*</span></label>
          <input
            v-model="password"
            type="password"
            placeholder="At least 8 characters"
            autocomplete="new-password"
            required
          />
        </div>
        <div class="form-group">
          <label>Confirm password <span class="req">*</span></label>
          <input
            v-model="confirm"
            type="password"
            placeholder="Repeat your password"
            autocomplete="new-password"
            required
          />
        </div>
        <button type="submit" class="onboard-btn" :disabled="loading">
          <span v-if="loading">Activating…</span>
          <span v-else>Activate Account</span>
        </button>
      </form>

      <p class="onboard-footer">
        Already have an account?
        <router-link to="/login">Sign in</router-link>
      </p>
    </div>
  </div>
</template>

<style scoped>
.onboard-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  padding: 24px;
}

.onboard-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 40px 36px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.06);
}

.onboard-logo {
  text-align: center;
  margin-bottom: 28px;
}
.logo-text {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.5px;
}
.dot { color: #ef4444; }

.onboard-title {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 6px;
  text-align: center;
}
.onboard-sub {
  font-size: 14px;
  color: #64748b;
  text-align: center;
  margin: 0 0 24px;
  line-height: 1.5;
}

.onboard-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  margin-bottom: 18px;
}

.onboard-form { display: flex; flex-direction: column; gap: 16px; }

.form-group { display: flex; flex-direction: column; gap: 6px; }
.form-group label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}
.form-group input {
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  padding: 11px 14px;
  font-size: 14px;
  color: #0f172a;
  outline: none;
  transition: border-color 0.15s;
}
.form-group input:focus { border-color: #0061eb; }

.onboard-btn {
  margin-top: 4px;
  background: #0061eb;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 13px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
}
.onboard-btn:hover:not(:disabled) { background: #0047b3; }
.onboard-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.onboard-footer {
  text-align: center;
  font-size: 13px;
  color: #64748b;
  margin-top: 20px;
}
.onboard-footer a { color: #0061eb; text-decoration: none; font-weight: 600; }
.req { color: #ef4444; }
</style>
